/**
 * CadRibbon — section-specific Command Manager for the CAD view.
 *
 * Sits below the ChipStrip and above the section content. Mounted only when
 * activeSection === 'cad'. Other sections may add their own ribbons later;
 * this is the first one.
 *
 * Layout: button groups separated by vertical dividers.
 *   BOM        — Extract / View / Upload / Reconcile
 *   Trace      — Link / Suggest Links
 *   Compare    — What Changed?
 *   Review     — New Version / Drawing Dashboard
 *   Bulk       — (only when ≥2 files selected)
 *
 * Heights:
 *   - Expanded: 72px (icon stacked over label)
 *   - Collapsed: 36px (icon-only) — toggled via the rightmost handle
 */
import React, { useState, useEffect } from 'react';
import { Box, Tooltip, IconButton, Divider, Typography } from '@mui/material';
import {
    AutoAwesome,
    AutoFixHigh,
    CloudUpload,
    Dashboard,
    Difference,
    ExpandLess,
    ExpandMore,
    FormatListBulleted,
    Link as LinkIcon,
    NoteAdd,
    PlayArrow,
    Send,
    AutoMode,
    LocalOffer,
    Clear,
} from '@mui/icons-material';

const EXPANDED_HEIGHT = 72;
const COLLAPSED_HEIGHT = 36;

/**
 * RibbonButton — icon + label stacked vertically (expanded) or just icon
 * (collapsed). Tooltip always shows the label so collapsed mode is discoverable.
 *
 * @param {string} label - Visible text in expanded mode + tooltip.
 * @param {ReactNode} icon - MUI icon.
 * @param {boolean} disabled
 * @param {string} disabledTooltip - Shown when disabled instead of label.
 * @param {function} onClick
 * @param {boolean} collapsed - Hides the label.
 */
const RibbonButton = ({ label, icon, disabled, disabledTooltip, onClick, collapsed }) => {
    const tip = disabled && disabledTooltip ? disabledTooltip : label;
    const button = (
        <Box
            component="button"
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: collapsed ? 0 : 0.25,
                minWidth: collapsed ? 32 : 56,
                px: 1,
                py: 0.5,
                background: 'transparent',
                border: 'none',
                borderRadius: 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                color: 'text.primary',
                transition: 'background 0.15s',
                '&:hover': disabled ? {} : { background: 'action.hover' },
            }}
        >
            <Box sx={{ display: 'flex', '& svg': { fontSize: collapsed ? 18 : 22 } }}>
                {icon}
            </Box>
            {!collapsed && (
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        textAlign: 'center',
                        maxWidth: 70,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </Typography>
            )}
        </Box>
    );
    return (
        <Tooltip title={tip} placement="bottom">
            <span>{button}</span>
        </Tooltip>
    );
};

const GroupDivider = () => (
    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
);

/**
 * CadRibbon — receives handlers from App.js / UnifiedCADView. All handlers
 * are optional; missing handler ⇒ the button is disabled with an explanatory
 * tooltip. This lets us land the skeleton first and wire each group in
 * subsequent commits without breaking the layout.
 */
const CadRibbon = ({
    // Single-action context — the file the user clicked in the file list.
    // The CAD viewer's dropdown is just a render target; the file list is
    // canonical for ribbon actions.
    selectedFile,
    // Multi-select context — array of file IDs the user checkboxed.
    selectedFileIds = [],
    // BOM
    onExtractBOM,
    onViewBOM,
    onUploadXLSX,
    onReconcile,
    // Trace
    onLink,
    onSuggestLinks,
    // Compare
    onWhatChanged,
    // Review
    onNewVersion,
    onOpenDashboard,
    // Bulk
    onBulkExtract,
    onBulkSubmit,
    onBulkTag,
    onBulkClear,
}) => {
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('eis_cad_ribbon_collapsed') === '1'; } catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem('eis_cad_ribbon_collapsed', collapsed ? '1' : '0'); } catch { /* ignore */ }
    }, [collapsed]);

    // "hasTarget" — the user has clicked something in the file list. Most
    // single-action buttons require a target. (Variable kept named hasDrawing
    // below for minimal churn — semantically it's "has a target file".)
    const hasDrawing = !!selectedFile?.id;
    const bulkCount = selectedFileIds.length;
    const hasBulk = bulkCount >= 2;

    // Tooltip for actions that need a selected drawing
    const needsDrawing = 'Select a drawing first';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                height: collapsed ? COLLAPSED_HEIGHT : EXPANDED_HEIGHT,
                px: 1,
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                overflowX: 'auto',
                transition: 'height 0.15s',
            }}
        >
            {/* BOM */}
            <RibbonButton
                label="Extract BOM"
                icon={<AutoAwesome />}
                disabled={!hasDrawing || !onExtractBOM}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Not wired yet'}
                onClick={() => onExtractBOM?.(selectedFile)}
                collapsed={collapsed}
            />
            <RibbonButton
                label="View BOM"
                icon={<FormatListBulleted />}
                disabled={!hasDrawing || !onViewBOM}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Not wired yet'}
                onClick={() => onViewBOM?.(selectedFile)}
                collapsed={collapsed}
            />
            <RibbonButton
                label="Upload XLSX"
                icon={<CloudUpload />}
                disabled={!hasDrawing || !onUploadXLSX}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Not wired yet'}
                onClick={() => onUploadXLSX?.(selectedFile)}
                collapsed={collapsed}
            />
            <RibbonButton
                label="Reconcile"
                icon={<PlayArrow />}
                disabled={!hasDrawing || !onReconcile}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Not wired yet'}
                onClick={() => onReconcile?.(selectedFile)}
                collapsed={collapsed}
            />

            <GroupDivider />

            {/* Trace */}
            <RibbonButton
                label="Link"
                icon={<LinkIcon />}
                disabled={!hasDrawing || !onLink}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Not wired yet'}
                onClick={() => onLink?.(selectedFile)}
                collapsed={collapsed}
            />
            <RibbonButton
                label="Suggest Links"
                icon={<AutoFixHigh />}
                disabled={!hasDrawing || !onSuggestLinks}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Not wired yet'}
                onClick={() => onSuggestLinks?.(selectedFile)}
                collapsed={collapsed}
            />

            <GroupDivider />

            {/* Compare */}
            <RibbonButton
                label="What Changed?"
                icon={<Difference />}
                disabled={!hasDrawing || !onWhatChanged}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Coming soon — AI version explainer'}
                onClick={() => onWhatChanged?.(selectedFile)}
                collapsed={collapsed}
            />

            <GroupDivider />

            {/* Review */}
            <RibbonButton
                label="New Version"
                icon={<NoteAdd />}
                disabled={!hasDrawing || !onNewVersion}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Not wired yet'}
                onClick={() => onNewVersion?.(selectedFile)}
                collapsed={collapsed}
            />
            <RibbonButton
                label="Dashboard"
                icon={<Dashboard />}
                disabled={!hasDrawing || !onOpenDashboard}
                disabledTooltip={!hasDrawing ? needsDrawing : 'Coming soon — drawing dashboard'}
                onClick={() => onOpenDashboard?.(selectedFile)}
                collapsed={collapsed}
            />

            {/* Bulk group — only when ≥2 files selected */}
            {hasBulk && (
                <>
                    <GroupDivider />
                    <RibbonButton
                        label={`Extract (${bulkCount})`}
                        icon={<AutoMode />}
                        disabled={!onBulkExtract}
                        disabledTooltip="Not wired yet"
                        onClick={onBulkExtract}
                        collapsed={collapsed}
                    />
                    <RibbonButton
                        label={`Submit (${bulkCount})`}
                        icon={<Send />}
                        disabled={!onBulkSubmit}
                        disabledTooltip="Not wired yet"
                        onClick={onBulkSubmit}
                        collapsed={collapsed}
                    />
                    <RibbonButton
                        label={`Tag (${bulkCount})`}
                        icon={<LocalOffer />}
                        disabled={!onBulkTag}
                        disabledTooltip="Not wired yet"
                        onClick={onBulkTag}
                        collapsed={collapsed}
                    />
                    <RibbonButton
                        label="Clear"
                        icon={<Clear />}
                        onClick={onBulkClear}
                        collapsed={collapsed}
                    />
                </>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* Collapse / expand handle */}
            <Tooltip title={collapsed ? 'Expand ribbon' : 'Collapse ribbon'} placement="bottom">
                <IconButton
                    size="small"
                    onClick={() => setCollapsed((v) => !v)}
                    sx={{ alignSelf: 'center', mx: 0.5 }}
                >
                    {collapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default CadRibbon;
